/**
 * Một event mỗi lần người dùng bấm in tem lô, và một event nữa cho kết quả.
 *
 * Câu hỏi cần trả lời: tem lô có thực sự được in ở xưởng không, in qua luồng
 * nào (in thẳng trên desktop hay đẩy vào hàng đợi cho máy in ở kho), và bao
 * nhiêu lần bấm in mà không ra được tem. DB không trả lời được: bảng print
 * queue chỉ có job của luồng hàng đợi, còn nhánh in local không để lại dấu
 * vết nào phía server.
 *
 * Mã lô không rời khỏi máy: nó chỉ ra đúng một lô chè cụ thể. Chỉ gửi loại
 * lô, luồng in và kết quả dạng enum.
 */
import { getAnalyticsActor, posthog } from './posthog'

const lotTypes = ['sorting', 'scenting', 'packaging', 'product'] as const
const modes = ['local', 'queue'] as const
const stages = ['clicked', 'succeeded', 'failed'] as const
const entryPoints = ['packaging_log_12g5'] as const

export type LotLabelLotType = (typeof lotTypes)[number]
/** `local`: in thẳng bằng hộp thoại in của browser. `queue`: đẩy job cho agent in ở kho. */
export type LotLabelPrintMode = (typeof modes)[number]
export type LotLabelPrintStage = (typeof stages)[number]
/**
 * `local_open_failed`: không mở được iframe in (thường do popup/iframe bị chặn).
 * `request_failed`: API tem lỗi — tải HTML hỏng hoặc enqueue hỏng.
 */
export type LotLabelPrintFailure = 'local_open_failed' | 'request_failed'
/**
 * Nút in đặt ở đâu, chỉ gửi cho nút in KHÁC các màn lô/phiếu kho.
 * `packaging_log_12g5`: nút in tem trên sổ đóng gói 12,5g (M-87).
 */
export type LotLabelEntryPoint = (typeof entryPoints)[number]

export interface LotLabelPrintEvent {
    lotType: LotLabelLotType
    mode: LotLabelPrintMode
    stage: LotLabelPrintStage
    failure?: LotLabelPrintFailure
    entryPoint?: LotLabelEntryPoint
}

/**
 * `succeeded` ở nhánh `local` chỉ nghĩa hộp thoại in của browser đã mở — người
 * dùng vẫn có thể bấm Huỷ; ở nhánh `queue` chỉ nghĩa job đã vào hàng đợi, chưa
 * chứng minh máy in ở kho đã nhả giấy. Analytics hỏng tuyệt đối không được
 * chặn việc in.
 */
export function captureLotLabelPrint(event: LotLabelPrintEvent) {
    try {
        const actor = getAnalyticsActor()
        if (!actor) return
        if (!lotTypes.includes(event.lotType)) return
        if (!modes.includes(event.mode)) return
        if (!stages.includes(event.stage)) return
        posthog.capture('lot_label_print', {
            schema_version: 1,
            event_id: crypto.randomUUID(),
            ...actor,
            lot_type: event.lotType,
            mode: event.mode,
            stage: event.stage,
            ...(event.failure ? { failure: event.failure } : {}),
            ...(event.entryPoint && entryPoints.includes(event.entryPoint)
                ? { entry_point: event.entryPoint }
                : {}),
        })
    } catch {
        // Analytics failure must not block printing.
    }
}
